import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppState } from '../../../client-redux/reducers';
import { LoginState } from '../../../client-redux/login/reducer';
import { Language } from '../../../languages/languageHandler';
import { Group, Loader, Table } from '@mantine/core';
import { getTicketStatus, getUrgencyType } from '../../utils';
import {
  fetchHelpdeskTickets,
  fetchTicketsXLS,
  IHelpdeskTicketsResponse
} from '../../../../libraries/helpdesk/tickets';
import Header from '../../../components/util/header';
import PaginationComponent from '../../../components/pagination';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { formatDateTimeNSecLabel, formatDateToISO } from '../../../utils/date';
import * as Icon from '@tabler/icons-react';
import './styles.scss';
import { endOfDay } from 'date-fns';

export enum TICKETSTATUS {
  ALL = 'all',
  OPEN = 'open',
  CLOSE = 'close'
}

const HelpdeskTicketList = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const login = useSelector<AppState, LoginState>(state => state.login);
  const controller = new AbortController();
  const navigate = useNavigate();

  const [list, setList] = useState<IHelpdeskTicketsResponse>();
  const [noResults, setNoResults] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 1,
    minHeightForDynamic: 400
  });
  const [totalRowsResponse, setTotalRowsResponse] = useState<number | undefined>();
  const [statusOperCode, setStatusOperCode] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();

  useEffect(() => {
    if (filterBarData && limit) {
      loadTicketsList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, offset, filterBarData]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };
  const mapTicketStatusToClosedParam = (status?: string) => {
    switch (status) {
      case TICKETSTATUS.OPEN:
        return 'false';
      case TICKETSTATUS.CLOSE:
        return 'true';
      default:
        return '';
    }
  };

  const loadTicketsList = () => {
    if (!login.token) return;

    if (loading) {
      setNoResults(false);
      controller.abort();
    }

    setLoading(true);
    const shouldFetchTotalRows = offset === 0;

    fetchHelpdeskTickets(
      controller,
      offset,
      limit,
      shouldFetchTotalRows,
      filterBarData?.ticketId,
      filterBarData?.eid,
      filterBarData?.entity,
      mapTicketStatusToClosedParam(filterBarData?.ticketStatus),
      formatDateToISO(filterBarData?.beginDateTime ?? undefined),
      formatDateToISO(filterBarData?.endDateTime)
    )
      .then(response => {
        if (response.statusOper.code === 0) {
          setList(response);
          if (shouldFetchTotalRows && typeof response.totalRows === 'number') {
            setTotalRowsResponse(response.totalRows);
          }
          setNoResults(false);
          setStatusOperCode(response.statusOper.code);
        } else {
          setNoResults(true);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const getXLSReport = () => {
    if (!filterBarData) return;

    return fetchTicketsXLS(
      controller,
      formatDateToISO(filterBarData.endDateTime) ?? '',
      filterBarData.ticketStatus ?? 'false',
      filterBarData.ticketId ?? undefined,
      filterBarData.eid ?? undefined,
      filterBarData.entity ?? undefined,
      formatDateToISO(filterBarData.beginDateTime ?? undefined) ?? ''
    );
  };

  const toggleNewTicket = () => {
    navigate(`/helpdesk/ticket/new/${filterBarData?.eid ? `?eid=${filterBarData.eid}` : ''}`);
  };

  return (
    <div className="helpdesk-tickets">
      <Header pageTitle={lang.GLOBAL_TICKETS} />

      <HelpdeskFilterBar
        onSearch={loadTicketsList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          ticketId: true,
          eid: true,
          entity: true,
          ticketStatus: true,
          beginDateTime: true,
          endDateTime: true
        }}
        initialData={{
          ticketStatus: TICKETSTATUS.OPEN,
          beginDateTime: undefined,
          endDateTime: endOfDay(new Date())
        }}
        xlsReport={getXLSReport}
        addButton={toggleNewTicket}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>{lang.GLOBAL_AGENT}</Table.Th>
              <Table.Th>{lang.GLOBAL_CLIENT}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_ID_MIN}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_SERIAL_NUMBER}</Table.Th>
              <Table.Th>{lang.GLOBAL_DATE}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_COM_STATE}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_COM_STATE}</Table.Th>
              <Table.Th>{lang.GLOBAL_TIME}</Table.Th>
              <Table.Th>{lang.GLOBAL_URGENCY}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {noResults ? (
                  <Table.Tr>
                    <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.tickets.map((ticket, index) => {
                    return (
                      <Table.Tr
                        key={index}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/helpdesk/ticket/read/${ticket.ticketID}`)}
                      >
                        <Table.Td>{ticket.ticketID}</Table.Td>
                        <Table.Td>{ticket.agent?.name}</Table.Td>
                        <Table.Td>{ticket.entityName}</Table.Td>
                        <Table.Td>{ticket.eid}</Table.Td>
                        <Table.Td>{ticket.sn}</Table.Td>
                        <Table.Td>{formatDateTimeNSecLabel(ticket.openDT)}</Table.Td>
                        <Table.Td className={ticket.status === 1 ? 'completed-ticket' : ''}>
                          {getTicketStatus(lang, ticket.status || 0).name}
                        </Table.Td>
                        <Table.Td>{formatDateTimeNSecLabel(ticket.statusDT)}</Table.Td>
                        <Table.Td>{ticket.time} min</Table.Td>
                        <Table.Td>{getUrgencyType(lang, ticket.urgency || 0)}</Table.Td>
                        <Table.Td>
                          <Icon.IconEdit
                            stroke={2}
                            style={{ cursor: 'pointer' }}
                            onClick={event => {
                              event.stopPropagation();
                              navigate(`/helpdesk/ticket/edit/${ticket.ticketID}`);
                            }}
                          />
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRowsResponse}
          statusOperCode={statusOperCode}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default HelpdeskTicketList;
